/**
 * Mixpanel Analytics Tracking Utility
 *
 * Provides helper functions to track custom events with Mixpanel
 * Usage: window.MixpanelTracker.trackEvent('Event Name', { property: 'value' })
 */

window.MixpanelTracker = (function() {
  'use strict';

  const isReady = () => {
    if (!window.mixpanelInitialized || !window.mixpanel) {
      return false;
    }
    return true;
  };

  return {
    /**
     * Track custom event
     * @param {string} eventName - Name of the event
     * @param {object} properties - Event properties
     */
    track: function(eventName, properties = {}) {
      if (!isReady()) return;

      try {
        mixpanel.track(eventName, properties);
      } catch (error) {
        console.error('[Mixpanel Tracker] Error tracking event:', error);
      }
    },

    /**
     * Track product view
     * @param {object} product - Product object
     */
    trackProductView: function(product) {
      if (!isReady()) return;

      const properties = {
        product_id: product.id,
        product_title: product.title,
        product_handle: product.handle,
        product_price: product.price / 100,
        product_vendor: product.vendor,
        product_type: product.product_type,
        product_tags: product.tags,
        has_variants: product.variants && product.variants.length > 1,
        variant_count: product.variants ? product.variants.length : 0
      };

      this.track('Product Viewed', properties);
    },

    /**
     * Track collection/category view
     * @param {object} collection - Collection object
     */
    trackCollectionView: function(collection) {
      if (!isReady()) return;

      const properties = {
        collection_id: collection.id,
        collection_title: collection.title,
        collection_handle: collection.handle,
        products_count: collection.products_count || 0
      };

      this.track('Collection Viewed', properties);
    },

    /**
     * Track search
     * @param {string} query - Search query
     * @param {number} results - Number of results
     */
    trackSearch: function(query, results = 0) {
      if (!isReady()) return;

      this.track('Search', {
        search_query: query,
        results_count: results
      });
    },

    /**
     * Track wishlist/favorite addition
     * @param {object} product - Product object
     */
    trackWishlistAdd: function(product) {
      if (!isReady()) return;

      const properties = {
        product_id: product.id,
        product_title: product.title,
        product_price: product.price / 100,
        product_vendor: product.vendor
      };

      this.track('Wishlist Added', properties);
    },

    /**
     * Track wishlist/favorite removal
     * @param {object} product - Product object
     */
    trackWishlistRemove: function(product) {
      if (!isReady()) return;

      const properties = {
        product_id: product.id,
        product_title: product.title
      };

      this.track('Wishlist Removed', properties);
    },

    /**
     * Track add to cart event (alternative to built-in tracking)
     * @param {object} product - Product object
     * @param {number} quantity - Quantity added
     * @param {string} variantId - Variant ID (optional)
     */
    trackAddToCart: function(product, quantity = 1, variantId = null) {
      if (!isReady()) return;

      const properties = {
        product_id: product.id,
        product_title: product.title,
        product_price: product.price / 100,
        product_vendor: product.vendor,
        quantity: quantity,
        variant_id: variantId
      };

      this.track('Add to Cart', properties);
    },

    /**
     * Track remove from cart
     * @param {object} product - Product object
     * @param {number} quantity - Quantity removed
     */
    trackRemoveFromCart: function(product, quantity = 1) {
      if (!isReady()) return;

      const properties = {
        product_id: product.id,
        product_title: product.title,
        product_price: product.price / 100,
        quantity: quantity
      };

      this.track('Remove from Cart', properties);
    },

    /**
     * Track checkout initiated
     * @param {object} cartData - Cart data
     */
    trackCheckoutInitiated: function(cartData) {
      if (!isReady()) return;

      const properties = {
        cart_total: cartData.total_price,
        item_count: cartData.item_count,
        items: cartData.items || []
      };

      this.track('Checkout Initiated', properties);
    },

    /**
     * Set user properties
     * @param {object} properties - User properties
     */
    setUserProperties: function(properties = {}) {
      if (!isReady()) return;

      try {
        mixpanel.people.set(properties);
      } catch (error) {
        console.error('[Mixpanel Tracker] Error setting user properties:', error);
      }
    },

    /**
     * Update user profile with subscription info
     * @param {object} subscription - Subscription data
     */
    trackSubscription: function(subscription) {
      if (!isReady()) return;

      const properties = {
        subscription_id: subscription.id,
        subscription_status: subscription.status,
        subscription_frequency: subscription.frequency,
        subscription_next_billing: subscription.next_billing_date
      };

      this.track('Subscription Created', properties);
      this.setUserProperties({
        subscription_status: subscription.status,
        subscription_frequency: subscription.frequency
      });
    },

    /**
     * Track page scroll depth
     * @param {number} depth - Scroll depth percentage (0-100)
     */
    trackScrollDepth: function(depth) {
      if (!isReady()) return;

      this.track('Page Scroll', {
        scroll_depth: depth
      });
    },

    /**
     * Track form submission
     * @param {string} formName - Form identifier/name
     * @param {object} formData - Form data (without sensitive info)
     */
    trackFormSubmission: function(formName, formData = {}) {
      if (!isReady()) return;

      const properties = {
        form_name: formName,
        ...formData
      };

      this.track('Form Submitted', properties);
    },

    /**
     * Track newsletter signup
     * @param {string} email - Email address
     * @param {string} source - Source/location of signup
     */
    trackNewsletterSignup: function(email, source = 'popup') {
      if (!isReady()) return;

      // Hash email for privacy
      const emailHash = this._hashEmail(email);

      const properties = {
        email_hash: emailHash,
        signup_source: source
      };

      this.track('Newsletter Signup', properties);
      this.setUserProperties({
        newsletter_subscriber: true,
        newsletter_signup_date: new Date().toISOString(),
        newsletter_signup_source: source
      });
    },

    /**
     * Track video engagement
     * @param {string} videoTitle - Title of the video
     * @param {string} action - Action: 'started', 'paused', 'completed'
     * @param {number} duration - Duration watched in seconds
     */
    trackVideoEngagement: function(videoTitle, action, duration = 0) {
      if (!isReady()) return;

      const properties = {
        video_title: videoTitle,
        video_action: action,
        duration_seconds: duration
      };

      this.track('Video Engagement', properties);
    },

    /**
     * Identify user
     * @param {string} userId - User ID
     * @param {object} properties - User properties
     */
    identifyUser: function(userId, properties = {}) {
      if (!isReady()) return;

      try {
        mixpanel.identify(userId);
        if (Object.keys(properties).length > 0) {
          mixpanel.people.set(properties);
        }
      } catch (error) {
        console.error('[Mixpanel Tracker] Error identifying user:', error);
      }
    },

    /**
     * Grant consent for tracking (GDPR)
     */
    grantConsent: function() {
      if (typeof window.grantMixpanelConsent === 'function') {
        window.grantMixpanelConsent();
      }
    },

    /**
     * Stop session recording
     */
    stopSessionRecording: function() {
      if (!isReady() || typeof mixpanel.stop_session_recording !== 'function') return;

      try {
        mixpanel.stop_session_recording();
      } catch (error) {
        console.error('[Mixpanel Tracker] Error stopping session recording:', error);
      }
    },

    /**
     * Start session recording
     */
    startSessionRecording: function() {
      if (!isReady() || typeof mixpanel.start_session_recording !== 'function') return;

      try {
        mixpanel.start_session_recording();
      } catch (error) {
        console.error('[Mixpanel Tracker] Error starting session recording:', error);
      }
    },

    /**
     * Opt out of tracking
     */
    optOut: function() {
      if (!isReady()) return;

      try {
        mixpanel.opt_out_tracking();
      } catch (error) {
        console.error('[Mixpanel Tracker] Error opting out:', error);
      }
    },

    /**
     * Opt in to tracking
     */
    optIn: function() {
      if (!isReady()) return;

      try {
        mixpanel.opt_in_tracking();
      } catch (error) {
        console.error('[Mixpanel Tracker] Error opting in:', error);
      }
    },

    /**
     * Get current session replay ID and properties
     * Useful for attaching to support tickets or external systems
     * @returns {object} Object with $mp_replay_id or empty object if no recording
     */
    getSessionReplayProperties: function() {
      if (!isReady() || typeof mixpanel.get_session_recording_properties !== 'function') {
        return {};
      }

      try {
        const properties = mixpanel.get_session_recording_properties();
        return properties;
      } catch (error) {
        console.error('[Mixpanel Tracker] Error getting session replay properties:', error);
        return {};
      }
    },

    /**
     * Get URL to view current replay in Mixpanel UI
     * Useful for debugging or adding to support tickets
     * @returns {string|null} Mixpanel replay URL or null if no recording
     */
    getSessionReplayUrl: function() {
      if (!isReady() || typeof mixpanel.get_session_replay_url !== 'function') {
        return null;
      }

      try {
        const url = mixpanel.get_session_replay_url();
        return url;
      } catch (error) {
        console.error('[Mixpanel Tracker] Error getting session replay URL:', error);
        return null;
      }
    },

    /**
     * Copy current session replay URL to clipboard
     * Useful for quick sharing with support teams
     */
    copySessionReplayUrlToClipboard: function() {
      const url = this.getSessionReplayUrl();
      if (!url) {
        console.warn('[Mixpanel Tracker] No active session replay to copy');
        return false;
      }

      try {
        navigator.clipboard.writeText(url);
        return true;
      } catch (error) {
        console.error('[Mixpanel Tracker] Error copying to clipboard:', error);
        return false;
      }
    },

    /**
     * Stop recording before accessing sensitive screen
     * Resume with startSessionRecording() when leaving
     * Use case: User accessing payment/account settings
     */
    pauseBeforeSensitiveContent: function() {
      this.stopSessionRecording();
    },

    /**
     * Resume recording after leaving sensitive screen
     * Use after calling pauseBeforeSensitiveContent()
     */
    resumeAfterSensitiveContent: function() {
      this.startSessionRecording();
    },

    /**
     * Force recording for specific user segment
     * Use case: Only record free plan users, or specific feature access
     * @param {boolean} shouldRecord - Whether to record this user
     */
    setRecordingForUserSegment: function(shouldRecord) {
      if (!isReady()) return;

      if (shouldRecord) {
        this.startSessionRecording();
      } else {
        this.stopSessionRecording();
      }
    },

    /**
     * Track user error/support escalation with replay link
     * Automatically includes current session replay ID
     * @param {string} errorMessage - Description of the error
     * @param {object} additionalData - Extra context (optional)
     */
    trackErrorWithReplay: function(errorMessage, additionalData = {}) {
      if (!isReady()) return;

      const replayProperties = this.getSessionReplayProperties();
      const properties = {
        error_message: errorMessage,
        replay_id: replayProperties.$mp_replay_id || 'no_replay',
        replay_url: this.getSessionReplayUrl(),
        ...additionalData
      };

      this.track('Support - Error Report', properties);
    },

    /**
     * Track support ticket creation with replay
     * Helps support team debug issues
     * @param {string} ticketId - Support ticket ID
     * @param {string} issue - Issue description
     * @param {object} metadata - Additional metadata
     */
    trackSupportTicketWithReplay: function(ticketId, issue, metadata = {}) {
      if (!isReady()) return;

      const replayProperties = this.getSessionReplayProperties();
      const properties = {
        ticket_id: ticketId,
        issue_description: issue,
        replay_id: replayProperties.$mp_replay_id,
        replay_url: this.getSessionReplayUrl(),
        timestamp: new Date().toISOString(),
        ...metadata
      };

      this.track('Support - Ticket Created', properties);
    },

    // Private method: Simple email hash for privacy
    _hashEmail: function(email) {
      // Simple hash using built-in crypto (not cryptographically secure but fine for this use)
      let hash = 0;
      for (let i = 0; i < email.length; i++) {
        const char = email.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return Math.abs(hash).toString(16);
    }
  };
})();